// Разблокировка и ключи доступа (WebAuthn): /api/auth/lock, /api/auth/unlock/*, /api/auth/passkeys.
//
// Отпечатком можно разблокировать и без сети: вызов берётся у сервера заранее
// (prefetchUnlockOptions), устройство подписывает его после отпечатка, приложение открывается,
// а подписанный ответ ждёт связи (submitPendingUnlock) — сервер снимет блокировку сессии сам.
// Паролем — только с сетью.

import {
  base64URLStringToBuffer,
  startAuthentication,
  startRegistration,
  type AuthenticationResponseJSON,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  type RegistrationResponseJSON,
} from '@simplewebauthn/browser'

import { api, ApiError } from '@/api/client'

import * as store from './store'

export type Passkey = {
  id: string
  credential_id: string
  name: string
  created_at: string
  last_used_at: string | null
}

type Ok = { ok: true }

export const lockKeys = { passkeys: ['auth', 'passkeys'] as const }

export const lockApi = {
  setLock: (lock_after_s: number | null) =>
    api.put<{ lock_after_s: number | null }>('/auth/lock', { lock_after_s }),
  lockNow: () => api.post<Ok>('/auth/lock/now'),
  unlockPassword: (password: string) => api.post<Ok>('/auth/unlock/password', { password }),
  unlockOptions: () => api.post<PublicKeyCredentialRequestOptionsJSON>('/auth/unlock/passkey/options'),
  unlockPasskey: (credential: unknown) => api.post<Ok>('/auth/unlock/passkey', { credential }),
  passkeys: () => api.get<Passkey[]>('/auth/passkeys'),
  passkeyOptions: (password: string) =>
    api.post<PublicKeyCredentialCreationOptionsJSON>('/auth/passkeys/options', { password }),
  addPasskey: (credential: RegistrationResponseJSON, name: string) =>
    api.post<Passkey>('/auth/passkeys', { credential, name }),
  deletePasskey: (id: string) => api.delete<Ok>(`/auth/passkeys/${id}`),
}

/** Ошибка разблокировки с текстом для человека. */
export class UnlockError extends Error {}

// Сервер принимает вызов 12 часов; берём с запасом.
const OPTIONS_MAX_AGE_MS = 11 * 60 * 60 * 1000
const OPTIONS_REFRESH_MS = 6 * 60 * 60 * 1000

function rememberOptions(options: PublicKeyCredentialRequestOptionsJSON): void {
  store.setUnlockOptions({ options, fetchedAt: Date.now() })
}

/**
 * Взять вызов разблокировки заранее — для отпечатка без сети. Новый вызов отменяет прежний,
 * поэтому, пока ответ без сети ждёт отправки, вызов не обновляется.
 */
export async function prefetchUnlockOptions(force = false): Promise<void> {
  if (!store.getPasskeyId() || store.getPendingUnlock() || store.isLocked()) return
  const stored = store.getUnlockOptions()
  if (!force && stored && Date.now() - stored.fetchedAt < OPTIONS_REFRESH_MS) return
  try {
    rememberOptions(await lockApi.unlockOptions())
  } catch {
    // нет сети или ключей на сервере — попробуем при следующем случае
  }
}

/** Устройство подтвердило человека (флаги UP и UV в authenticatorData). */
export function userVerified(response: AuthenticationResponseJSON): boolean {
  const data = new Uint8Array(base64URLStringToBuffer(response.response.authenticatorData))
  return data.length > 32 && (data[32]! & 0x05) === 0x05
}

/**
 * Понятная причина ошибки WebAuthn. Неизвестную показываем с именем ошибки браузера —
 * по нему видно, что чинить (на телефоне консоли нет).
 */
export function webauthnMessage(error: unknown, action: 'create' | 'get' = 'get'): string {
  if (error instanceof ApiError) return error.message
  if (!(error instanceof Error)) return 'Не получилось проверить отпечаток'
  const code = (error as { code?: string }).code
  switch (error.name) {
    case 'NotAllowedError':
      return action === 'create'
        ? 'Добавление отменено или время вышло. Если окно с отпечатком не появлялось — на телефоне не настроены ключи доступа (на Android: менеджер паролей Google, Сервисы Google Play)'
        : 'Отпечаток не подтверждён'
    case 'InvalidStateError':
      return 'Ключ на этом устройстве уже добавлен'
    case 'ConstraintError':
      return 'На телефоне не включена блокировка экрана (отпечаток, PIN или графический ключ) — без неё ключ не создать'
    case 'NotSupportedError':
      return 'Телефон или браузер не поддерживает ключи доступа'
    case 'SecurityError':
      if (code === 'ERROR_INVALID_RP_ID') {
        return `Сервис открыт не по тому адресу: ключ привязан к домену из APP_BASE_URL, а страница открыта на ${window.location.hostname}`
      }
      return 'Ключи доступа работают только по домену с https (не по IP-адресу)'
  }
  const base = action === 'create' ? 'Устройство не смогло создать ключ' : 'Не получилось проверить отпечаток'
  return `${base} (${error.name}${error.message ? `: ${error.message}` : ''})`
}

/** Разблокировать отпечатком: 'online' — сервер уже открыл сессию, 'offline' — откроет позже. */
export async function unlockWithPasskey(): Promise<'online' | 'offline'> {
  let options: PublicKeyCredentialRequestOptionsJSON | null = null
  let offline = typeof navigator !== 'undefined' && !navigator.onLine
  if (!offline) {
    try {
      options = await lockApi.unlockOptions()
      rememberOptions(options)
    } catch (error) {
      if (!(error instanceof ApiError && error.isNetwork)) throw new UnlockError(webauthnMessage(error))
      offline = true
    }
  }
  if (offline) {
    const stored = store.getUnlockOptions()
    if (!stored || Date.now() - stored.fetchedAt > OPTIONS_MAX_AGE_MS) {
      throw new UnlockError('Без сети отпечаток сейчас не сработает — подключись к интернету')
    }
    options = stored.options as PublicKeyCredentialRequestOptionsJSON
  }

  let response: AuthenticationResponseJSON
  try {
    response = await startAuthentication({ optionsJSON: options! })
  } catch (error) {
    throw new UnlockError(webauthnMessage(error))
  }
  if (!userVerified(response)) throw new UnlockError('Устройство не подтвердило отпечаток')

  if (offline) {
    store.setPendingUnlock(response)
    store.setUnlockOptions(null)
    store.setLocked(false)
    return 'offline'
  }
  try {
    await lockApi.unlockPasskey(response)
  } catch (error) {
    throw new UnlockError(webauthnMessage(error))
  } finally {
    store.setUnlockOptions(null) // вызов израсходован
  }
  store.setLocked(false)
  void prefetchUnlockOptions(true)
  return 'online'
}

export async function unlockWithPassword(password: string): Promise<void> {
  await lockApi.unlockPassword(password)
  store.setLocked(false)
  void prefetchUnlockOptions(true)
}

let submitting: Promise<boolean> | null = null

/** Отправить ответ ключа, полученный без сети. true — сервер открыл сессию. */
export function submitPendingUnlock(): Promise<boolean> {
  const pending = store.getPendingUnlock()
  if (!pending) return Promise.resolve(false)
  submitting ??= (async () => {
    try {
      await lockApi.unlockPasskey(pending)
      store.setPendingUnlock(null)
      void prefetchUnlockOptions(true)
      return true
    } catch (error) {
      if (!(error instanceof ApiError && error.isNetwork)) {
        // Сервер ответ не принял (вызов устарел или заменён) — нужен новый отпечаток.
        store.setPendingUnlock(null)
        store.setLocked(true)
      }
      return false
    } finally {
      submitting = null
    }
  })()
  return submitting
}

/** Ответ 423: сессия заблокирована. Если ждёт ответ ключа без сети — сначала отправить его. */
export function handleLocked(onUnlocked?: () => void): void {
  if (store.getPendingUnlock()) {
    void submitPendingUnlock().then((ok) => {
      if (ok) onUnlocked?.()
    })
    return
  }
  store.setLocked(true)
}

/** Добавить ключ доступа на этом устройстве (после пароля). */
export async function registerPasskey(password: string, name: string): Promise<Passkey> {
  const options = await lockApi.passkeyOptions(password)
  let credential: RegistrationResponseJSON
  try {
    credential = await startRegistration({ optionsJSON: options })
  } catch (error) {
    console.warn('WebAuthn create:', error)
    throw new UnlockError(webauthnMessage(error, 'create'))
  }
  const passkey = await lockApi.addPasskey(credential, name)
  store.setPasskeyId(passkey.credential_id)
  store.setUnlockOptions(null) // регистрация заменила вызов разблокировки на сервере
  void prefetchUnlockOptions(true)
  return passkey
}

/** Имя ключа по умолчанию — по устройству. */
export function defaultPasskeyName(userAgent = navigator.userAgent): string {
  if (/iPhone/.test(userAgent)) return 'iPhone'
  if (/iPad/.test(userAgent)) return 'iPad'
  if (/Android/.test(userAgent)) return 'Телефон Android'
  if (/Macintosh/.test(userAgent)) return 'Mac'
  if (/Windows/.test(userAgent)) return 'Компьютер Windows'
  return 'Устройство'
}
